if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        if (this._hasChangeListener) {
          return;
        }

        this._hasChangeListener = true;

        this.addEventListener(
          'change',
          (event) => {
            // Check if target is inside this element
            const targetInThisElement = this.contains(event.target);

            // Check if there's a nested variant-selects between this and target
            let hasNestedVariantSelects = false;
            let currentElement = event.target;

            while (currentElement && currentElement !== document.body) {
              if (currentElement.tagName === 'VARIANT-SELECTS') {
                if (currentElement !== this) {
                  hasNestedVariantSelects = true;
                  break;
                } else {
                  break;
                }
              }
              currentElement = currentElement.parentElement;
            }

            // Ignore if target is not in this element or there's a nested variant-selects
            if (!targetInThisElement || hasNestedVariantSelects) {
              return;
            }

            const target = this.getInputForEventTarget(event.target);
            this.updateSelectedSwatchValue(event);
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event,
                target,
                selectedOptionValues: this.resolvedOptionValues(event.target) || this.selectedOptionValues,
              },
            });
          },
          true
        ); // Use CAPTURE phase to catch events before they bubble
      }

      /*
        NativeDSD: Quality only lists what exists for the selected channel setup. When a channel setup is picked
        that doesn't exist in the current quality (e.g. Dolby Atmos = MKV 48 kHz only, 12ch = DXD only), switch to a
        quality that exists for it instead of falling back to stereo: DSD 256 if possible, otherwise the best one.
        Returns option value ids in option order, or null to keep the default behaviour.
      */
      resolvedOptionValues(changedElement) {
        try {
          if (!changedElement || changedElement.tagName !== 'SELECT') return null;
          const mapEl = this.querySelector('script[data-ndsd-option-map]');
          if (!mapEl) return null;
          const map = JSON.parse(mapEl.textContent);
          const variants = (map.variants || []).filter(Boolean);
          const names = map.options.map((o) => o.name.toLowerCase());
          const qi = names.indexOf('quality');
          const ci = names.indexOf('channels');
          if (qi < 0 || ci < 0) return null;
          const selects = map.options.map((o) => this.querySelector(`select[name="options[${o.name}]"]`));
          if (selects.some((sel) => !sel)) return null;
          if (changedElement !== selects[ci]) return null;

          const current = selects.map((sel) => sel.value);
          const exists = (vals) => variants.some((v) => v.every((val, i) => val === vals[i]));
          if (exists(current)) return null;

          const channel = current[ci];
          const candidates = variants.filter((v) => v[ci] === channel && v.every((val, i) => i === qi || i === ci || val === current[i]));
          if (!candidates.length) return null;
          const qualities = map.options[qi].values;
          const preferred = candidates.find((v) => v[qi] === 'DSD 256');
          const pick = preferred || candidates.sort((a, b) => String(qualities[a[qi]]?.rank).localeCompare(String(qualities[b[qi]]?.rank)))[0];

          return pick.map((val, i) => String(map.options[i].values[val]?.id ?? ''));
        } catch (error) {
          return null;
        }
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}

/* NativeDSD: help links with data-intercom-article open that article inside the Intercom messenger;
   without Intercom (blocked, not loaded) the normal link opens the help center in a new tab */
if (!window.ndsdIntercomArticleLinks) {
  window.ndsdIntercomArticleLinks = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-intercom-article]');
    if (!link || typeof window.Intercom !== 'function') return;
    event.preventDefault();
    window.Intercom('showArticle', Number(link.dataset.intercomArticle));
  });
}

/* NativeDSD: wishlist links. ?quality=…&channels=… (old WordPress codes like "dsd-64fs" / "2ch", or names like
   "DSD 64" / "2ch Stereo") opens the album on that version, via the ?variant= the page already supports.
   No match: the page stays on its default version. */
if (!window.ndsdQualityParam) {
  window.ndsdQualityParam = true;
  const params = new URLSearchParams(location.search);
  const wantQuality = params.get('quality');
  const wantChannels = params.get('channels');
  const productPath = location.pathname.match(/^(.*\/products\/[^/?#]+)/);
  if ((wantQuality || wantChannels) && !params.get('variant') && productPath) {
    // "DSD 64" and "dsd-64fs" both become "dsd64"; "2ch Stereo" becomes "2chstereo"
    const norm = (value) => String(value || '').toLowerCase().replace(/fs\b/g, '').replace(/[^a-z0-9]/g, '');
    // exact match first, else the shortest value that starts with it ("2ch" -> "2ch Stereo", "dxd" -> "DXD 24 Bit")
    const pick = (values, wanted) => {
      if (!wanted) return null;
      const w = norm(wanted);
      const exact = values.find((v) => norm(v) === w);
      if (exact) return exact;
      return values.filter((v) => norm(v).startsWith(w)).sort((a, b) => a.length - b.length)[0] || null;
    };
    fetch(`${productPath[1]}.js`)
      .then((response) => response.json())
      .then((product) => {
        const qIndex = product.options.findIndex((o) => /quality/i.test(o.name));
        const cIndex = product.options.findIndex((o) => /channel/i.test(o.name));
        const quality = qIndex > -1 ? pick(product.options[qIndex].values, wantQuality) : null;
        const channels = cIndex > -1 ? pick(product.options[cIndex].values, wantChannels) : null;
        // a quality this album doesn't have (e.g. DSD 1024): keep the page's own default
        if (wantQuality && !quality) return;
        const matches = (v) =>
          (!quality || v.options[qIndex] === quality) && (!channels || v.options[cIndex] === channels);
        const variant =
          product.variants.find((v) => matches(v) && v.available) || product.variants.find(matches);
        if (!variant || (!quality && !channels)) return;
        params.delete('quality');
        params.delete('channels');
        params.set('variant', variant.id);
        location.replace(`${location.pathname}?${params.toString()}${location.hash}`);
      })
      .catch(() => {});
  }
}

/* NativeDSD: the wishlist app reads the version from its button, which only ever held the album's first
   version (FLAC 192). Just before it reads it, fill in the version selected now, with its quality and channels. */
if (!window.ndsdWishlistVariant) {
  window.ndsdWishlistVariant = true;
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-ndsd-wl-add]');
      if (!button) return;
      const scope = button.closest('.product__sidebar-wrapper, .product__info-wrapper, .product') || document;
      const idInput = scope.querySelector('form[action*="/cart/add"] [name="id"]');
      const quality = scope.querySelector('select[name="options[Quality]"]');
      const channels = scope.querySelector('select[name="options[Channels]"]');
      if (idInput && idInput.value) button.setAttribute('data-variant-id', `gid://shopify/ProductVariant/${idInput.value}`);
      if (quality) button.setAttribute('data-quality', quality.value);
      if (channels) button.setAttribute('data-channels', channels.value);
    },
    true
  );
}
